<!-- CourseBuilder Sidebar -->
<aside class="sidebar-container bg-gradient-to-b from-primary/5 to-secondary/5 border-r border-primary/20" role="navigation" aria-label="Main navigation" data-testid="sidebar-section">

  <!-- User Profile Section -->
  <div class="user-profile-section bg-base-100/90 backdrop-blur-sm p-4 border-b border-primary/10 relative z-[100]">
    <!-- Main User Info Card -->
    <div class="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-4 shadow-sm border border-primary/20">
      <div class="flex items-center gap-3">
        <div class="avatar placeholder">
          <div class="bg-gradient-to-r from-primary to-accent text-primary-content rounded-full w-11 h-11">
            <span class="text-lg font-semibold">{{ currentUser?.name?.charAt(0) || 'U' }}</span>
          </div>
        </div>
        <div class="flex-1 min-w-0">
          <h3 class="font-semibold text-primary truncate">{{ currentUser?.name || 'User' }}</h3>
          <p class="text-sm text-base-content opacity-60 truncate">{{ currentUser?.email }}</p>
        </div>
      </div>
    </div>

    <!-- Organization Switcher -->
    <div class="mt-3">
      <app-organization-switcher></app-organization-switcher>
    </div>
  </div>

  <!-- Navigation Menu -->
  <div class="navigation-scroll p-4 relative z-[10]">
    <ul class="menu menu-lg w-full">

      <!-- Dashboard -->
      <li>
        <a routerLink="/dashboard" routerLinkActive="active" class="flex items-center gap-3 hover:bg-primary/10" data-testid="sidebar-dashboard-link">
          <app-icon name="dashboard" size="sm" class="text-primary"></app-icon>
          Dashboard
        </a>
      </li>

      <!-- My Courses - Shows both learner and instructor courses -->
      <li>
        <a routerLink="/courses" routerLinkActive="active" class="flex items-center gap-3 hover:bg-secondary/10" data-testid="sidebar-my-courses-link">
          <app-icon name="school" size="sm" class="text-secondary"></app-icon>
          My Courses
        </a>
      </li>

      <!-- Course Catalog (KAN-385) - every member can browse and buy the active
           org's purchasable courses; instructors see what their learners see. -->
      <li>
        <a routerLink="/catalog" routerLinkActive="active" class="flex items-center gap-3 hover:bg-secondary/10" data-testid="sidebar-catalog-link">
          <app-icon name="storefront" size="sm" class="text-secondary"></app-icon>
          Course Catalog
        </a>
      </li>

      <!-- My Organization - Only for instructors -->
      @if (canAccessInstructor()) {
        <li>
          <a [routerLink]="'/instructor/organization/' + selectedOrgId" routerLinkActive="active" class="flex items-center gap-3 hover:bg-primary/10" data-testid="sidebar-my-orgs-link">
            <app-icon name="business" size="sm" class="text-primary"></app-icon>
            My Organization
          </a>
        </li>
      }

      <!-- Bookmarks -->
      <li>
        <a routerLink="/bookmarks" routerLinkActive="active" class="flex items-center gap-3 hover:bg-warning/10" data-testid="sidebar-bookmarks-link">
          <app-icon name="bookmark" size="sm" class="text-warning"></app-icon>
          Bookmarks
        </a>
      </li>

      <!-- Profile -->
      <li>
        <a routerLink="/profile" routerLinkActive="active" class="flex items-center gap-3 hover:bg-accent/10" data-testid="sidebar-profile-link">
          <app-icon name="person" size="sm" class="text-accent"></app-icon>
          Profile
        </a>
      </li>

      <!-- My Credentials -->
      <li>
        <a routerLink="/my-credentials" routerLinkActive="active" class="flex items-center gap-3 hover:bg-success/10" data-testid="sidebar-my-credentials-link">
          <app-icon name="verified" size="sm" class="text-success"></app-icon>
          My Credentials
        </a>
      </li>

      <!-- Dues (KAN-357 task 11.5c) - a member's own dues page. Always shown;
           the page renders a neutral "no dues program" state when the member's
           org has no active dues plan and no membership. -->
      <li>
        <a routerLink="/dues" routerLinkActive="active" class="flex items-center gap-3 hover:bg-success/10" data-testid="sidebar-dues-link">
          <app-icon name="payments" size="sm" class="text-success"></app-icon>
          Dues
        </a>
      </li>

      <!-- Invitations (UI polish audit #2: the page is a list of pending
           course + org invitations, not a generic notifications feed). -->
      <li>
        <a routerLink="/invitations" routerLinkActive="active" class="flex items-center gap-3 hover:bg-secondary/10" data-testid="sidebar-notifications-link">
          <div class="indicator">
            <app-icon name="mail" size="sm" class="text-secondary"></app-icon>
            @if (notificationCount > 0) {
              <span class="badge badge-sm badge-primary indicator-item">
                {{ notificationCount > 99 ? '99+' : notificationCount }}
              </span>
            }
          </div>
          Invitations
        </a>
      </li>

      <!-- Help Center -->
      <li>
        <a routerLink="/help" routerLinkActive="active" class="flex items-center gap-3 hover:bg-info/10" data-testid="sidebar-help-link">
          <app-icon name="help" size="sm" class="text-info"></app-icon>
          Help Center
        </a>
      </li>

      <!-- Instructor Section -->
      @if (canAccessInstructor()) {
        <div>
          <li class="menu-title">
            <span class="flex items-center gap-2 text-primary font-semibold">
              <app-icon name="settings" size="sm" class="text-primary"></app-icon>
              Instructor Tools
            </span>
          </li>
          <!-- Primary Content Workflow -->
          <li *hasPermission="'content.modules.create'">
            <a routerLink="/instructor/content-hub" routerLinkActive="active" class="flex items-center gap-3 hover:bg-primary/10" data-testid="sidebar-content-hub-link">
              <app-icon name="hub" size="sm" class="text-primary"></app-icon>
              Content Hub
            </a>
          </li>
          <!-- Question Banks (KAN-346) -->
          <li *hasPermission="'content.modules.create'">
            <a routerLink="/instructor/question-banks" routerLinkActive="active" class="flex items-center gap-3 hover:bg-primary/10" data-testid="sidebar-question-banks-link">
              <app-icon name="library_books" size="sm" class="text-primary"></app-icon>
              Question Banks
            </a>
          </li>
          <!-- Gradebook -->
          <li *hasPermission="'gradebook.access'">
            <a routerLink="/instructor/gradebook" routerLinkActive="active" class="flex items-center gap-3 hover:bg-success/10" data-testid="sidebar-gradebook-link">
              <app-icon name="grading" size="sm" class="text-success"></app-icon>
              Gradebook
            </a>
          </li>
          <!-- Compliance Dashboard -->
          <li *hasPermission="'gradebook.access'">
            <a routerLink="/instructor/compliance" routerLinkActive="active" class="flex items-center gap-3 hover:bg-info/10" data-testid="sidebar-compliance-link">
              <app-icon name="fact_check" size="sm" class="text-info"></app-icon>
              Compliance Dashboard
            </a>
          </li>
          <!-- Credential Management -->
          <li *hasPermission="'gradebook.access'">
            <a routerLink="/instructor/credentials" routerLinkActive="active" class="flex items-center gap-3 hover:bg-success/10" data-testid="sidebar-credential-mgmt-link">
              <app-icon name="verified_user" size="sm" class="text-success"></app-icon>
              Credential Management
            </a>
          </li>
          <!-- Events -->
          <li *hasPermission="'gradebook.access'">
            <a routerLink="/instructor/events" routerLinkActive="active" class="flex items-center gap-3 hover:bg-info/10" data-testid="sidebar-events-link">
              <app-icon name="event" size="sm" class="text-info"></app-icon>
              Events
            </a>
          </li>
          <!-- Course Access Management -->
          <li *hasPermission="'course.assign_users'">
            <a routerLink="/instructor/course-access" routerLinkActive="active" class="flex items-center gap-3 hover:bg-warning/10" data-testid="sidebar-course-access-link">
              <app-icon name="group_add" size="sm" class="text-warning"></app-icon>
              Course Access
            </a>
          </li>
          <!-- Course Building Workflow -->
          <li *hasPermission="'course.create'">
            <a routerLink="/instructor/courses/create" routerLinkActive="active" class="flex items-center gap-3 hover:bg-secondary/10" data-testid="sidebar-create-course-link">
              <app-icon name="add_circle" size="sm" class="text-secondary"></app-icon>
              Create New Course
            </a>
          </li>
          <!-- Certificate Templates -->
          <li *hasPermission="'certificates.templates'">
            <a routerLink="/instructor/certificate-templates" routerLinkActive="active" class="flex items-center gap-3 hover:bg-info/10" data-testid="sidebar-certificate-templates-link">
              <app-icon name="workspace_premium" size="sm" class="text-info"></app-icon>
              Certificate Templates
            </a>
          </li>
        </div>
      }

      <!-- Admin Section -->
      @if (canAccessAdmin()) {
        <div>
          <li class="menu-title">
            <span class="flex items-center gap-2 text-error font-semibold">
              <app-icon name="admin_panel_settings" size="sm" class="text-error"></app-icon>
              Admin Tools
            </span>
          </li>
          <li *hasPermission="'system.settings'">
            <a routerLink="/admin/dashboard" routerLinkActive="active" class="flex items-center gap-3 hover:bg-error/10" data-testid="sidebar-admin-dashboard-link">
              <app-icon name="admin_panel_settings" size="sm" class="text-error"></app-icon>
              Admin Dashboard
            </a>
          </li>
          <li *hasPermission="'org.create'">
            <a routerLink="/admin/organizations" routerLinkActive="active" class="flex items-center gap-3 hover:bg-info/10" data-testid="sidebar-admin-orgs-link">
              <app-icon name="business" size="sm" class="text-info"></app-icon>
              Organizations
            </a>
          </li>
          <li *hasPermission="'org.assign_super_moderator'">
            <a routerLink="/admin/instructors" routerLinkActive="active" class="flex items-center gap-3 hover:bg-warning/10" data-testid="sidebar-admin-instructors-link">
              <app-icon name="group" size="sm" class="text-warning"></app-icon>
              Instructors
            </a>
          </li>
        </div>
      }
    </ul>
  </div>

  <!-- Debug Tools -->
  <div class="px-4 pb-2">
    <app-debug-menu></app-debug-menu>
  </div>

  <!-- Bottom Actions -->
  <div class="sidebar-bottom p-4 bg-gradient-to-r from-base-200 to-base-300 border-t border-primary/20">
    <!-- Show user status if not authenticated -->
    @if (!isAuthenticated()) {
      <div class="mb-3 p-3 bg-gradient-to-r from-warning/20 to-error/20 rounded-lg text-center border border-warning/30">
        <p class="text-sm text-warning font-medium">Session expired</p>
      </div>
    }

    <!-- Always show logout button when authenticated -->
    <button
      (click)="logout()"
      class="btn btn-error w-full hover:btn-error shadow-lg"
      [class.btn-disabled]="!isAuthenticated()"
      aria-label="Logout"
      data-testid="sidebar-logout-btn">
      <app-icon name="logout" size="sm"></app-icon>
      {{ isAuthenticated() ? 'Logout' : 'Login Required' }}
    </button>

    <!-- Optional: Add refresh button -->
    @if (isAuthenticated()) {
      <button
        (click)="refreshUserData()"
        class="btn btn-ghost btn-sm w-full mt-2 hover:bg-primary/10 hover:text-primary"
        title="Refresh user data"
        data-testid="sidebar-refresh-btn">
        <app-icon name="refresh" size="sm"></app-icon>
        Refresh
      </button>
    }
  </div>

</aside>